<template>
  <NuxtLayout name="default-public">
    <div>
      <v-row>
        <v-col cols="12">
          <h1
            v-heading
            v-if="params.city && !params.category"
          >
            {{ locations.pagination?.totalItems }} Geschäfte in {{ params.city }}
          </h1>
          <h1
            v-heading
            v-else-if="params.city && params.category"
          >
            <!-- TODO fix capitalization -->
            {{ params.category }} in {{ params.city }}
          </h1>
          <h1
            v-heading
            v-else
          >Katalog</h1>

          <p>Entdecken Sie unseren umfassenden Katalog der besten Geschäfte in Ihrer Nähe. Finden Sie die besten
            Angebote und Dienstleistungen in Ihrer Umgebung.</p>
        </v-col>

        <v-col
          cols="12"
          v-if="locations.items?.length > 0 || search"
        >
          <!-- search bar -->
          <v-row>
            <v-col
              cols="12"
              :md="category ? 6 : 12"
            >
              <v-text-field
                v-model="search"
                label="Suche"
                outlined
                clearable
                @click:clear="loadLocations()"
                @input="delayedLoadLocations"
              >
                <template v-slot:prepend>
                  <v-icon>mdi-magnify</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col
              v-if="category"
              cols="12"
              :md="category ? 6 : 12"
            >
              <v-select
                v-model="category"
                label="Kategorie"
                :items="categories"
                item-title="name"
                outlined
                clearable
                @change="loadLocations"
              ></v-select>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <v-divider></v-divider>
          <v-progress-linear
            v-if="isLoading"
            style="position: fixed; top: 0; left: 0; width: 100%;"
            indeterminate
            color="accent"
          ></v-progress-linear>
        </v-col>

        <v-col
          cols="12"
          md="6"
        >
          <v-row>
            <v-col
              cols="12"
              v-for="location in locations.items"
              :key="location.id"
            >

              <LocationListCard :location="location" />
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="12"
          md="6"
          class="d-none d-md-block map-container"
          ref="mapContainer"
          v-if="locations.items?.length > 0"
        >
          <ClientOnly
            class="sticky-map"
            ref="stickyMap"
            :style="mapStyle"
          >
            <GoogleMap :locations="locations.items" />
          </ClientOnly>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          v-if="locations.items?.length > 0"
        >
          <!-- TODO add endless scroll -->
          <v-pagination
            v-model="page"
            @update:model-value="loadLocations"
            :length="locations.pagination?.totalPages ?? 1"
          ></v-pagination>
        </v-col>
        <v-col
          v-else
          cols="12"
        >
          <span
            class="text-xl"
            v-if="search"
          >
            Sie haben nach "{{ search }}" gesucht?
            <br>
            <br>
          </span>

          Keine Geschäfte gefunden, versuchen Sie doch mal <NuxtLink to="/katalog">mit dem Katalog</NuxtLink>. Und
          wir kümmern uns darum.
        </v-col>
      </v-row>

      <LinkBlockAroundYou />
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import headerGenerator from '@/utils/headerGenerator';
import { type Category, type CompanyLocation } from '@/types';
import { useLocationStore } from '~/store/locationStore';
import type { Pagination } from '~/composables/useDataTable';
import GoogleMap from '~/components/GoogleMap.vue';
import LocationListCard from '~/components/public/LocationListCard.vue';
import LinkBlockAroundYou from '~/components/public/LinkBlockAroundYou.vue';
import throttle from 'lodash/throttle';

useHead({
  ...headerGenerator(null, null, 'Katalog'),
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: 'Entdecken Sie unseren umfassenden Katalog der besten Geschäfte in Ihrer Nähe. Finden Sie die besten Angebote und Dienstleistungen in Ihrer Umgebung.'
    },
    {
      hid: 'keywords',
      name: 'keywords',
      content: 'Geschäfte, Dienstleistungen, Angebote, Katalog, lokale Geschäfte, beste Geschäfte, in der Nähe'
    },
    // {
    //   hid: 'og:title',
    //   property: 'og:title',
    //   content: 'Katalog der besten Geschäfte in Ihrer Nähe'
    // },
    // {
    //   hid: 'og:description',
    //   property: 'og:description',
    //   content: 'Durchstöbern Sie unseren Katalog und entdecken Sie die besten Geschäfte und Dienstleistungen in Ihrer Umgebung.'
    // },
    // {
    //   hid: 'og:type',
    //   property: 'og:type',
    //   content: 'website'
    // },
    // {
    //   hid: 'og:url',
    //   property: 'og:url',
    //   content: process.env.DOMAIN + '/listings'
    // },
    // {
    //   hid: 'og:image',
    //   property: 'og:image',
    //   content: process.env.DOMAIN + '/images/katalog-og-image.jpg'
    // },
    // {
    //   hid: 'twitter:card',
    //   name: 'twitter:card',
    //   content: 'summary_large_image'
    // },
    // {
    //   hid: 'twitter:title',
    //   name: 'twitter:title',
    //   content: 'Katalog der besten Geschäfte in Ihrer Nähe'
    // },
    // {
    //   hid: 'twitter:description',
    //   name: 'twitter:description',
    //   content: 'Durchstöbern Sie unseren Katalog und entdecken Sie die besten Geschäfte und Dienstleistungen in Ihrer Umgebung.'
    // },
    // {
    //   hid: 'twitter:image',
    //   name: 'twitter:image',
    //   content: process.env.DOMAIN + '/images/katalog-twitter-image.jpg'
    // }
  ]
});

const mapContainer = ref<HTMLElement | null>(null);
const stickyMap = ref(null);
const mapOffsetTop = ref(0);
const isSticky = ref(false);
const isLoading = ref(false);
const category = ref<typeof Category | null>(null);
const categories = ref<typeof Category[]>([]);
const search = ref<string | null>(null);

const updateScroll = () => {
  try {
    if (mapContainer.value) {
      const containerRect = mapContainer.value.$el.getBoundingClientRect();


      isSticky.value = containerRect.top <= 0;
    }
  } catch (e) {
    console.error(e);
  }
};

const mapStyle = computed(() => {
  if (isSticky.value) {
    return {
      position: 'fixed',
      top: '0px',
      width: `${mapContainer.value.$el.offsetWidth}px`,
      height: '100vh',
      overflowY: 'auto'
    };
  }
  return {};
});

const locationStore = useLocationStore();
const locations = ref<{
  items: typeof CompanyLocation[],
  pagination: Pagination
}>({
  items: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    page: 1,
    pageSize: 12
  }
});

const route = useRoute();
const router = useRouter();
const params = route.params;
const page = ref<number>(Number(route.query.page) || 1);

const loadLocations = async (page?: number) => {
  isLoading.value = true;

  if (search.value) {
    search.value = search.value.trim();
  }

  locations.value = await locationStore.getPublicLocations(page ?? 1, 12, null, {
    city: params.city,
    category: params.category,
    search: search.value,
  });

  router.push({ query: { page: page ?? 1 } });

  window?.scrollTo({ top: 0, behavior: 'smooth' });
  isLoading.value = false;

  setTimeout(() => {
    if (mapContainer.value) {
      mapOffsetTop.value = mapContainer.value.offsetTop;
      window.addEventListener('scroll', updateScroll);
      window.addEventListener('resize', updateScroll);
      updateScroll(); // Initial check
    }
  }, 100);
}

const loadCategories = async () => {
  categories.value = await locationStore.loadCategories();
}

const delayedLoadLocations = throttle(() => loadLocations(1), 1000);

await loadLocations(page.value);
loadCategories();

onUnmounted(() => {
  window.removeEventListener('scroll', updateScroll);
  window.removeEventListener('resize', updateScroll);
});
</script>

<style scoped lang="scss">
.map-container {
  position: relative;
  height: 100vh;
}

.sticky-map {
  height: 100vh;
  overflow: hidden;
}

:deep(.v-rating__wrapper) {
  margin-right: 4px;
}
</style>
